import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { inr, tierLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export default function Cart() {
  const { cart, updateCartQty, removeFromCart, cartSubtotal, config } = useStore();
  const nav = useNavigate();
  const threshold = config.free_delivery_threshold || 3000;
  const remaining = Math.max(0, threshold - cartSubtotal);
  const pct = Math.min(100, (cartSubtotal / threshold) * 100);

  if (cart.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-4 py-32 text-center">
        <ShoppingBag size={56} className="mx-auto text-[#3B251D]" />
        <h1 className="font-display text-4xl mt-6">Your cart is empty</h1>
        <p className="text-[#8C7B6E] mt-3">Discover our master chocolatier confections.</p>
        <Link to="/shop" className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-full mt-8">Browse Chocolates <ArrowRight size={18} /></Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
      <h1 className="font-display text-4xl sm:text-5xl mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-4">
          {/* Free delivery bar */}
          <div className="lux-card p-5">
            {remaining > 0 ? (
              <p className="text-sm text-[#C8B8AB] mb-3">Add <span className="text-[#D4AF37] font-semibold">{inr(remaining)}</span> more for complimentary delivery</p>
            ) : (
              <p className="text-sm text-[#2E7D32] mb-3 font-semibold">✓ You've unlocked complimentary delivery!</p>
            )}
            <Progress value={pct} className="h-2 bg-[#1A0F0B]" />
          </div>

          {cart.map((it) => (
            <div key={`${it.product_id}-${it.pieces}`} className="lux-card p-4 flex gap-4" data-testid={`cart-item-${it.slug}-${it.pieces}`}>
              <img src={it.image} alt={it.name} className="h-24 w-24 rounded-xl object-cover" />
              <div className="flex-1">
                <div className="flex justify-between">
                  <div>
                    <Link to={`/product/${it.slug}`} className="font-display text-lg hover:text-[#D4AF37]">{it.name}</Link>
                    <p className="text-[#8C7B6E] text-sm">{tierLabel(it.pieces)} · {inr(it.price)}</p>
                  </div>
                  <button onClick={() => removeFromCart(it.product_id, it.pieces)} data-testid={`cart-remove-${it.slug}-${it.pieces}`} className="text-[#8C7B6E] hover:text-[#DC2626]"><Trash2 size={18} /></button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-[#3B251D] rounded-full">
                    <button onClick={() => updateCartQty(it.product_id, it.pieces, it.qty - 1)} className="h-9 w-9 flex items-center justify-center text-[#D4AF37]"><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm">{it.qty}</span>
                    <button onClick={() => updateCartQty(it.product_id, it.pieces, it.qty + 1)} className="h-9 w-9 flex items-center justify-center text-[#D4AF37]"><Plus size={14} /></button>
                  </div>
                  <span className="font-display text-lg text-[#D4AF37]">{inr(it.price * it.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lux-card p-6 h-max lg:sticky lg:top-28">
          <h3 className="font-display text-xl mb-5">Order Summary</h3>
          <div className="flex justify-between text-[#C8B8AB] mb-3"><span>Subtotal</span><span data-testid="cart-subtotal">{inr(cartSubtotal)}</span></div>
          <div className="flex justify-between text-[#C8B8AB] mb-3"><span>Delivery</span><span>{cartSubtotal >= threshold ? "Free" : inr(config.delivery_fee || 150)}</span></div>
          <p className="text-xs text-[#8C7B6E] mb-5">Coupons applied at checkout.</p>
          <div className="border-t border-[#3B251D] pt-4 flex justify-between font-display text-xl">
            <span>Total</span><span className="text-[#D4AF37]">{inr(cartSubtotal + (cartSubtotal >= threshold ? 0 : (config.delivery_fee || 150)))}</span>
          </div>
          <Button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="btn-gold w-full py-4 rounded-full mt-6">Proceed to Checkout</Button>
        </div>
      </div>
    </div>
  );
}
